import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { SupportApiService } from '../core/support-api.service';
import { Visibility } from '../core/models';

/** Spec, §Internal vs. public separation — the console's single most important
 * safety control. Two separate buttons, never one toggle (point 1); the box
 * re-tints for the whole time the agent types (point 2); a confirmation gate
 * ONLY on the internal-draft -> reply mid-draft switch (point 3), the one
 * moment content actually crosses the visibility boundary. Server-side
 * enforcement (routers/tickets.py rejecting a customer-authored internal
 * message) is the actual guarantee (point 5) — this component is for
 * preventing the honest mistake. */
@Component({
  selector: 'app-compose',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltip],
  template: `
    <div class="compose-actions">
      <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="open-reply" (click)="open('public')"
              [class.is-active]="activeVisibility === 'public'" [disabled]="sending()"
              ngbTooltip="Compose a reply the requester will see">
        <span data-testid="reply-button" aria-hidden="true">✉</span> Reply to customer
      </button>
      @if (!replyOnly) {
        <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm note-btn" data-testid="open-internal-note" (click)="open('internal')"
                [class.is-active]="activeVisibility === 'internal'" [disabled]="sending()"
                ngbTooltip="Only your team can see this — never the requester.">
          <span data-testid="internal-note-button" aria-hidden="true">🔒</span> Add internal note
        </button>
      }
    </div>

    @if (activeVisibility) {
      <div data-testid="compose-box" class="compose-box" [class.compose-internal]="activeVisibility === 'internal'">
        @if (activeVisibility === 'internal') {
          <span data-testid="internal-persistent-label" class="internal-persistent-label">
            🔒 Internal — not visible to requester
          </span>
        }
        @if (pendingSwitchConfirm) {
          <div data-testid="visibility-switch-confirm" class="switch-confirm">
            <p>This will be sent to the customer. Continue?</p>
            <div class="switch-confirm__actions">
              <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="confirm-switch-yes" (click)="confirmSwitch()">Yes, switch to reply</button>
              <button type="button" class="bt-btn bt-btn--ghost bt-btn--sm" data-testid="confirm-switch-no" (click)="cancelSwitch()">Cancel</button>
            </div>
          </div>
        } @else {
          <textarea class="bt-textarea" [(ngModel)]="body" data-testid="compose-textarea"
                    [disabled]="sending()"
                    [placeholder]="activeVisibility === 'internal' ? 'Write an internal note for your team…' : 'Write a reply to the requester…'"></textarea>
          <div class="compose-box__send">
            <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="send-button" (click)="send()"
                    [disabled]="sending()"
                    [ngbTooltip]="activeVisibility === 'internal' ? 'Save this internal note' : 'Send this reply to the requester'">{{ sending() ? 'Sending…' : 'Send' }}</button>
          </div>
          @if (sendError()) {
            <p data-testid="send-error" class="compose-error" role="alert">{{ sendError() }}</p>
          }
        }
      </div>
    }
  `,
  styles: [`
    .compose-actions { display: flex; gap: 0.5rem; margin-bottom: 0.6rem; }
    .compose-actions .is-active { box-shadow: 0 0 0 2px color-mix(in srgb, var(--bt-border-focus) 45%, transparent); }
    .compose-box { border: 1px solid var(--bt-border-default); border-radius: 8px; padding: 0.75rem; background: var(--bt-bg-surface); }
    .compose-internal { background: color-mix(in srgb, var(--bt-status-warning) 10%, var(--bt-bg-surface)); border-color: color-mix(in srgb, var(--bt-status-warning) 55%, transparent); }
    .internal-persistent-label { display: block; font-size: 12px; font-weight: 700; color: color-mix(in srgb, var(--bt-status-warning) 80%, #6b4e00); margin-bottom: 0.4rem; }
    .compose-box__send { display: flex; justify-content: flex-end; margin-top: 0.5rem; }
    .switch-confirm p { margin: 0 0 0.5rem; font-size: 14px; color: var(--bt-text-primary); }
    .switch-confirm__actions { display: flex; gap: 0.5rem; }
    .compose-error { margin: 0.5rem 0 0; font-size: 13px; color: var(--bt-status-error, #b42318); }
  `],
})
export class ComposeComponent {
  private api = inject(SupportApiService);

  @Input() ticketId = '';
  // VAR-safe projection (Task 18): a VAR principal's only compose action is a
  // customer-facing reply — the internal-note button must be genuinely absent
  // from the DOM in this mode, not merely hidden or disabled.
  @Input() replyOnly = false;
  @Output() messagePosted = new EventEmitter<void>();

  activeVisibility: Visibility | null = null;
  // Backed by a signal rather than a plain field: a signal write always
  // notifies its template consumer synchronously, even when set directly on
  // the component instance (as the spec's tests do) outside any zone-tracked
  // event. Angular 21's default (non-exhaustive) checkNoChanges pass only
  // re-visits a view whose reactive consumer is flagged dirty; a plain field
  // mutation between two `detectChanges()` calls does not flag it, which
  // produces a false-positive NG0100 (ExpressionChangedAfterItHasBeenChecked)
  // even though nothing is actually wrong. The get/set pair keeps `body` a
  // normal string property from every caller's perspective.
  private bodySignal = signal('');
  get body(): string {
    return this.bodySignal();
  }
  set body(value: string) {
    this.bodySignal.set(value);
  }
  pendingSwitchConfirm = false;
  // A failed send must NEVER silently discard the draft. `sendError` surfaces the
  // failure to the agent (the reply POST 401s on an expired staff token, and the old
  // subscribe() had no error handler — the message just sat in the box). `sending`
  // disables the button + guards against a double-post.
  readonly sendError = signal<string | null>(null);
  readonly sending = signal(false);

  open(visibility: Visibility): void {
    this.sendError.set(null);
    if (this.activeVisibility === 'internal' && visibility === 'public' && this.body.trim().length > 0) {
      // Spec point 3: the internal->public boundary that matters — an
      // in-progress internal draft is about to be sent to the customer.
      this.pendingSwitchConfirm = true;
      return;
    }
    this.activeVisibility = visibility;
    this.pendingSwitchConfirm = false;
  }

  confirmSwitch(): void {
    this.activeVisibility = 'public';
    this.pendingSwitchConfirm = false;
  }

  cancelSwitch(): void {
    this.pendingSwitchConfirm = false;
  }

  send(): void {
    if (!this.activeVisibility || !this.body.trim() || this.sending()) return;
    this.sendError.set(null);
    this.sending.set(true);
    this.api.postMessage(this.ticketId, this.body, this.activeVisibility).subscribe({
      next: () => {
        this.body = '';
        this.activeVisibility = null;
        this.sending.set(false);
        this.messagePosted.emit();
      },
      error: (err: unknown) => {
        // Surface the failure instead of dropping the reply — the draft is kept so the
        // agent can retry. A 401/403 is almost always an expired staff session.
        this.sending.set(false);
        const status = (err as { status?: number } | null)?.status;
        this.sendError.set(
          status === 401 || status === 403
            ? 'Couldn’t send — your session may have expired. Refresh the page and try again. Your draft is kept.'
            : 'Couldn’t send — please try again. Your draft is kept.',
        );
      },
    });
  }
}
